import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X, Lock, Check, Film, ArrowLeft, ArrowRight, Download, Sparkles, Music2, Pencil, RefreshCw, Layers } from "lucide-react";
import { pipelinesApi, streamAsset, mistApi } from "@/api/mist";
import VaultPickButton from "@/components/mist/VaultPickButton";
import UniversalSaveModal, { emergencyCommit } from "@/components/mist/UniversalSaveModal";
import { RenderProgress } from "@/components/mist/RenderProgress";
import RSystemStudio from "@/components/mist/RSystemStudio";

const MAX_SCENES = 6;
const MAX_CHARS = 3;
const btn = "font-mono text-[10px] font-black uppercase tracking-widest border-2 border-black flex items-center justify-center gap-1.5";

export default function SequentialDirector({ onExit }) {
  const [mode, setMode] = useState("standard");   // standard 6-scene | rsystem pre-production
  const toggle = (
    <div className="flex items-center gap-0.5 border-2 border-black bg-black p-0.5" data-testid="seq-mode-toggle">
      <button onClick={() => setMode("standard")} data-testid="seq-mode-standard"
        className={`font-mono text-[8px] font-black uppercase tracking-widest px-2 py-1 flex items-center gap-1 ${mode === "standard" ? "bg-gold text-black" : "text-white/50"}`}>
        <Film size={10} /> 6-Scene
      </button>
      <button onClick={() => setMode("rsystem")} data-testid="seq-mode-rsystem"
        className={`font-mono text-[8px] font-black uppercase tracking-widest px-2 py-1 flex items-center gap-1 ${mode === "rsystem" ? "bg-[#C9B6FF] text-black" : "text-white/50"}`}>
        <Layers size={10} /> R-System
      </button>
    </div>
  );
  return mode === "standard"
    ? <StandardDirector onExit={onExit} headerExtra={toggle} />
    : <RSystemStudio onExit={onExit} headerExtra={toggle} />;
}

function StandardDirector({ onExit, headerExtra }) {
  const [phase, setPhase] = useState("script");        // script | render
  const [chars, setChars] = useState([]);              // [{id, url}]
  const [charBusy, setCharBusy] = useState(false);
  const [prompts, setPrompts] = useState(["", "", "", "", "", ""]);
  const [aspect, setAspect] = useState("9:16");
  const [sceneSeconds, setSceneSeconds] = useState(8);   // per scene, hard cap 10s
  const [matchMusic, setMatchMusic] = useState(false);
  const [music, setMusic] = useState(null);              // {id, name}
  const [musicBusy, setMusicBusy] = useState(false);
  const [sid, setSid] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [busyIdx, setBusyIdx] = useState(null);
  const [editIdx, setEditIdx] = useState(null);
  const [editText, setEditText] = useState("");
  const [stage, setStage] = useState("");
  const [srcs, setSrcs] = useState({});
  const [stitching, setStitching] = useState(false);
  const [filmSrc, setFilmSrc] = useState(null);
  const [filmId, setFilmId] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [scenePct, setScenePct] = useState(null);
  const charRef = useRef(null);
  const musicRef = useRef(null);

  // Revoke all blob: object URLs on unmount so repeat visits don't leak memory.
  const cleanupRef = useRef({ chars: [], srcs: {}, filmSrc: null });
  useEffect(() => { cleanupRef.current = { chars, srcs, filmSrc }; }, [chars, srcs, filmSrc]);
  useEffect(() => () => {
    const { chars: c, srcs: s, filmSrc: f } = cleanupRef.current;
    c.forEach((x) => x.url && URL.revokeObjectURL(x.url));
    Object.values(s).forEach((u) => u && URL.revokeObjectURL(u));
    if (f) URL.revokeObjectURL(f);
  }, []);

  const loadSrc = useCallback(async (assetId) => {
    if (!assetId) return;
    setSrcs((cur) => { if (cur[assetId]) return cur; streamAsset(assetId).then((u) => setSrcs((s) => ({ ...s, [assetId]: u }))).catch(() => {}); return cur; });
  }, []);

  useEffect(() => {
    scenes.forEach((s) => { if (s.status === "completed" && s.video_asset_id) loadSrc(s.video_asset_id); });
  }, [scenes, loadSrc]);

  const uploadChar = async (e) => {
    const file = e.target.files?.[0]; if (e.target) e.target.value = "";
    if (!file) return;
    if (chars.length >= MAX_CHARS) { toast.error(`Up to ${MAX_CHARS} characters`); return; }
    setCharBusy(true);
    try {
      const r = await pipelinesApi.seqUploadCharacter(file);
      setChars((c) => [...c, { id: r.id, url: URL.createObjectURL(file) }]);
    } catch (err) { toast.error(err?.response?.data?.detail || "Character upload failed"); }
    finally { setCharBusy(false); }
  };

  const uploadMusic = async (e) => {
    const file = e.target.files?.[0]; if (e.target) e.target.value = "";
    if (!file) return;
    setMusicBusy(true);
    try {
      const r = await pipelinesApi.seqUploadMusic(file);
      setMusic({ id: r.id, name: file.name });
    } catch (err) { toast.error(err?.response?.data?.detail || "Music upload failed"); }
    finally { setMusicBusy(false); }
  };

  const runPoll = async (seqId, jobId, index) => {
    setBusyIdx(index); setStage("Starting…"); setScenePct(0);
    try {
      const j = await pipelinesApi.poll(jobId, { onTick: (t) => { setStage(t.stage || "Rendering…"); setScenePct(typeof t.progress === "number" ? t.progress : null); } });
      if (j.status !== "completed") throw new Error(j.error || "Scene render failed");
      const fresh = await pipelinesApi.seqGet(seqId); setScenes(fresh.scenes);
      toast.success(`Scene ${index + 1} ready`);
    } catch (e) {
      const fresh = await pipelinesApi.seqGet(seqId).catch(() => null);
      if (fresh) setScenes(fresh.scenes);
      toast.error(e?.response?.data?.detail || e.message || "Scene render failed");
    } finally { setBusyIdx(null); setStage(""); setScenePct(null); }
  };

  const startSequence = async () => {
    const ordered = [];
    for (const p of prompts.map((x) => x.trim())) { if (!p) break; ordered.push(p); }
    if (!ordered.length) { toast.error("Write Scene 1 to begin"); return; }
    setBusyIdx(0);
    try {
      const seq = await pipelinesApi.seqCreate({ characters: chars.map((c) => c.id), scenes: ordered, aspect,
        scene_seconds: sceneSeconds, match_music: matchMusic, music_id: music?.id || null });
      setSid(seq.id); setScenes(seq.scenes); setPhase("render");
      const g = await pipelinesApi.seqSceneGenerate(seq.id, 0);
      runPoll(seq.id, g.job_id, 0);
    } catch (e) { setBusyIdx(null); toast.error(e?.response?.data?.detail || "Couldn't start the sequence"); }
  };

  const generateScene = async (index) => {
    try { const g = await pipelinesApi.seqSceneGenerate(sid, index); runPoll(sid, g.job_id, index); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't render this scene"); }
  };

  const remakeScene = async (index, newPrompt) => {
    try {
      const g = await pipelinesApi.seqSceneGenerate(sid, index, (newPrompt || "").trim());
      setEditIdx(null);
      runPoll(sid, g.job_id, index);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remake this scene"); }
  };

  const approveAndNext = async (index) => {
    try {
      const seq = await pipelinesApi.seqSceneApprove(sid, index); setScenes(seq.scenes);
      const nxt = index + 1;
      if (nxt < seq.scenes.length) { const g = await pipelinesApi.seqSceneGenerate(sid, nxt); runPoll(sid, g.job_id, nxt); }
      else toast.success("All scenes approved — combine into a film below");
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };

  const stitchFilm = async () => {
    setStitching(true);
    try {
      const g = await pipelinesApi.seqStitch(sid);
      const j = await pipelinesApi.poll(g.job_id, {});
      if (j.status !== "completed") throw new Error(j.error || "Stitch failed");
      const u = await streamAsset(j.asset_id); setFilmSrc(u); setFilmId(j.asset_id);
      toast.success("Film stitched");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Stitch failed"); }
    finally { setStitching(false); }
  };

  useEffect(() => {
    if (!filmId || saved) return undefined;
    const h = () => emergencyCommit(filmId);
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [filmId, saved]);
  // MD §2 Law 4 — explicit purge only: drop the staged film so the user can re-stitch.
  const clearFilm = async () => {
    if (filmId && !saved) { try { await mistApi.discardAsset(filmId); } catch { /* ignore */ } }
    setFilmSrc(null); setFilmId(null); setSaved(false);
  };

  const allDone = scenes.length > 0 && scenes.every((s) => s.status === "completed");

  return (
    <div className="h-full w-full flex flex-col overflow-hidden" data-testid="seq-mode-root">
      <div className="flex items-center gap-2 px-4 pt-2 pb-1">
        <button onClick={onExit} data-testid="seq-back" className="text-white/60 hover:text-white"><ArrowLeft size={16} /></button>
        <Film size={15} className="text-gold" />
        <span className="font-serif text-lg text-white leading-none">Sequential Director</span>
        <div className="ml-auto">{headerExtra}</div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-6 space-y-4">
        {/* CAST */}
        <div className="border-2 border-black bg-[#141414] p-3">
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-white/55 mb-2">Cast — up to {MAX_CHARS} characters</p>
          <div className="flex items-center gap-2">
            {chars.map((c) => (
              <div key={c.id} className="relative h-16 w-16 border-2 border-gold overflow-hidden" data-testid={`seq-char-${c.id}`}>
                <img src={c.url} alt="" className="h-full w-full object-cover" />
                {phase === "script" && (
                  <button onClick={() => setChars((cc) => cc.filter((x) => x.id !== c.id))} data-testid={`seq-char-remove-${c.id}`}
                    className="absolute top-0 right-0 bg-black/70 text-white p-0.5"><X size={11} /></button>
                )}
              </div>
            ))}
            {phase === "script" && chars.length < MAX_CHARS && (
              <button onClick={() => charRef.current?.click()} disabled={charBusy} data-testid="seq-char-add"
                className="h-16 w-16 border-2 border-dashed border-white/30 text-white/50 grid place-items-center hover:border-gold">
                {charBusy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              </button>
            )}
            <input ref={charRef} type="file" accept="image/*" hidden onChange={uploadChar} data-testid="seq-char-input" />
          </div>
          {phase === "script" && chars.length < MAX_CHARS && (
            <div className="mt-2 flex items-center gap-2">
              <span className="font-mono text-[8px] uppercase tracking-widest text-white/35">Add from</span>
              <button onClick={() => charRef.current?.click()} disabled={charBusy} data-testid="seq-char-device"
                className="flex items-center gap-1.5 border-2 border-white/25 bg-black text-white/70 px-3 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest active:translate-y-[1px] disabled:opacity-50">
                <Upload size={12} /> Device
              </button>
              <VaultPickButton bucket="images" label="From Vault" title="Pick a cast photo" testid="seq-char-vault"
                disabled={charBusy} onFile={(f) => uploadChar({ target: { files: [f] } })} />
            </div>
          )}
          <p className="mt-2 font-mono text-[8px] text-white/40">These faces are carried into Scene 1 and every cut after it.</p>
        </div>

        {/* SCRIPT PHASE */}
        {phase === "script" && (
          <>
            <div className="flex items-center gap-1.5">
              {["9:16", "1:1", "16:9"].map((a) => (
                <button key={a} onClick={() => setAspect(a)} data-testid={`seq-aspect-${a.replace(":", "-")}`}
                  className={`${btn} flex-1 py-1.5 ${aspect === a ? "bg-gold text-black" : "bg-[#1a1a1a] text-white/60"}`}>{a}</button>
              ))}
            </div>

            <div className="border-2 border-black bg-[#141414] p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[9px] font-black uppercase tracking-widest text-white/55">Seconds per scene</span>
                <span className="font-mono text-[12px] font-black text-gold" data-testid="seq-duration-value">{sceneSeconds}s</span>
              </div>
              <input type="range" min={3} max={10} step={1} value={sceneSeconds} data-testid="seq-duration"
                onChange={(e) => setSceneSeconds(Number(e.target.value))} className="w-full accent-gold" />
              <p className="font-mono text-[8px] text-white/40">Hard cap 10s — keeps a 6-scene film inside the 60s reel grid.</p>
            </div>

            <div className="border-2 border-black bg-[#141414] p-3 space-y-2">
              <button onClick={() => setMatchMusic((v) => !v)} data-testid="seq-matchmusic-toggle"
                className={`${btn} w-full py-2 ${matchMusic ? "bg-[#00F5D4] text-black" : "bg-[#1a1a1a] text-white/60"}`}>
                <Music2 size={13} /> Match music to scene length · {matchMusic ? "ON" : "OFF"}
              </button>
              {matchMusic && (
                <>
                  <div className="flex items-center gap-2">
                    <button onClick={() => musicRef.current?.click()} disabled={musicBusy} data-testid="seq-music-upload"
                      className={`${btn} flex-1 py-2 bg-gold text-black disabled:opacity-60`}>
                      {musicBusy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {music ? "Change (Device)" : "Device"}
                    </button>
                    <VaultPickButton bucket="audio" label="From Vault" title="Pick a track from your Vault" testid="seq-music-vault"
                      disabled={musicBusy}
                      onItem={(it) => { setMusic({ id: it.id, name: it.filename || "Vault track" }); toast.success("Track picked from Vault"); }} />
                  </div>
                  {music && (
                    <p className="font-mono text-[9px] text-[#00F5D4] flex items-center gap-1" data-testid="seq-music-name">
                      <Check size={11} /> {music.name}
                    </p>
                  )}
                  <p className="font-mono text-[8px] text-white/40">Each scene gets its slot of the song with a 1.5s fade-out.</p>
                  <input ref={musicRef} type="file" accept="audio/*" hidden onChange={uploadMusic} data-testid="seq-music-input" />
                </>
              )}
            </div>
            {prompts.map((p, i) => {
              const locked = i > 0 && !prompts[i - 1].trim();
              return (
                <div key={i} className={`border-2 border-black ${locked ? "opacity-40" : ""} bg-[#141414] p-2`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="font-mono text-[9px] font-black uppercase tracking-widest text-gold">Scene {i + 1}</span>
                    {locked && <Lock size={10} className="text-white/40" />}
                  </div>
                  <textarea rows={2} value={p} disabled={locked} data-testid={`seq-prompt-${i}`}
                    onChange={(e) => setPrompts((arr) => arr.map((x, idx) => (idx === i ? e.target.value : x)))}
                    placeholder={i === 0 ? "Open on… describe the first shot" : "What happens next?"}
                    className="w-full resize-none bg-black/40 border border-white/10 px-2 py-1.5 font-mono text-[11px] text-white placeholder:text-white/25 focus:border-gold focus:outline-none" />
                </div>
              );
            })}
            <button onClick={startSequence} disabled={busyIdx !== null || !prompts[0].trim()} data-testid="seq-start-btn"
              className={`${btn} w-full bg-gold text-black py-3 disabled:opacity-50`}>
              {busyIdx !== null ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Lock Script & Generate Scene 1
            </button>
          </>
        )}

        {/* RENDER PHASE */}
        {phase === "render" && scenes.map((s, i) => {
          const rendering = busyIdx === i;
          const vsrc = s.video_asset_id ? srcs[s.video_asset_id] : null;
          const nextLocked = i + 1 < scenes.length && scenes[i + 1].status === "locked";
          return (
            <div key={i} data-testid={`seq-scene-${i}`}
              className={`border-2 border-black bg-[#141414] p-3 ${s.status === "locked" ? "opacity-45" : ""}`}>
              <div className="flex items-center gap-1.5 mb-2">
                <span className="font-mono text-[9px] font-black uppercase tracking-widest text-gold">Scene {i + 1}</span>
                <span data-testid={`seq-scene-status-${i}`} className={`font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${
                  s.status === "completed" ? "border-[#00F5D4] text-[#00F5D4]" :
                  s.status === "processing" ? "border-gold text-gold" :
                  s.status === "locked" ? "border-white/20 text-white/40" : "border-white/40 text-white/60"}`}>
                  {rendering ? "rendering" : s.status}
                </span>
                {s.status === "locked" && <Lock size={11} className="text-white/40" />}
              </div>
              <p className="font-mono text-[10px] text-white/60 mb-2 line-clamp-2">{s.prompt}</p>

              {rendering ? (
                <div className="flex flex-col items-center gap-2 py-4" data-testid={`seq-scene-rendering-${i}`}>
                  <RenderProgress value={scenePct} label={`${stage || "Rendering"} · scene ${i + 1}/${scenes.length}`} />
                </div>
              ) : s.status === "completed" ? (
                <>
                  {vsrc ? (
                    <video src={vsrc} controls playsInline data-testid={`seq-scene-video-${i}`}
                      className="w-full max-h-64 border-2 border-black bg-black" />
                  ) : (
                    <div className="py-6 text-center text-white/40 font-mono text-[10px]"><Loader2 size={14} className="animate-spin inline" /> loading preview…</div>
                  )}
                  {nextLocked && (
                    <button onClick={() => approveAndNext(i)} disabled={busyIdx !== null} data-testid={`seq-scene-approve-${i}`}
                      className={`${btn} w-full bg-[#00F5D4] text-black py-2.5 mt-2 disabled:opacity-50`}>
                      <Check size={13} /> Approve & Generate Next Scene <ArrowRight size={13} />
                    </button>
                  )}
                  {editIdx === i ? (
                    <div className="mt-2 space-y-1.5">
                      <textarea rows={2} value={editText} data-testid={`seq-scene-edit-input-${i}`}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full resize-none bg-black/40 border border-white/10 px-2 py-1.5 font-mono text-[11px] text-white focus:border-gold focus:outline-none" />
                      <div className="flex gap-1.5">
                        <button onClick={() => remakeScene(i, editText)} disabled={busyIdx !== null || !editText.trim()} data-testid={`seq-scene-remake-${i}`}
                          className={`${btn} flex-1 bg-gold text-black py-2 disabled:opacity-50`}>
                          <RefreshCw size={12} /> Remake Scene
                        </button>
                        <button onClick={() => setEditIdx(null)} data-testid={`seq-scene-editcancel-${i}`}
                          className={`${btn} px-3 bg-[#1a1a1a] text-white/60 py-2`}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => { setEditIdx(i); setEditText(s.prompt || ""); }} disabled={busyIdx !== null} data-testid={`seq-scene-edit-${i}`}
                      className={`${btn} w-full bg-[#1a1a1a] text-white/70 py-2 mt-2 disabled:opacity-50`}>
                      <Pencil size={12} /> Edit &amp; Remake Scene
                    </button>
                  )}
                </>
              ) : s.status === "idle" ? (
                <button onClick={() => generateScene(i)} disabled={busyIdx !== null} data-testid={`seq-scene-generate-${i}`}
                  className={`${btn} w-full bg-gold text-black py-2.5 disabled:opacity-50`}>
                  <Sparkles size={13} /> Generate Scene {i + 1}
                </button>
              ) : (
                <p className="font-mono text-[9px] text-white/40 py-2">Approve the previous scene to unlock.</p>
              )}
            </div>
          );
        })}

        {/* STITCH */}
        {phase === "render" && allDone && (
          <div className="border-2 border-gold bg-gold/10 p-3 space-y-2">
            <p className="font-mono text-[10px] font-black uppercase tracking-widest text-gold">All {scenes.length} scenes done</p>
            {filmSrc ? (
              <>
                <video src={filmSrc} controls playsInline data-testid="seq-film-video" className="w-full max-h-72 border-2 border-black bg-black" />
                <button onClick={() => setSaveOpen(true)} data-testid="seq-film-save" className={`${btn} w-full bg-white text-black py-2.5`}><Download size={13} /> {saved ? "Saved ✓ · Save again" : "Save Film"}</button>
                <button onClick={clearFilm} data-testid="seq-film-clear" className={`${btn} w-full bg-black text-gold border-2 border-gold py-2`}><RefreshCw size={12} /> Clear & Re-stitch</button>
                {!saved && <p className="font-mono text-[8px] uppercase tracking-widest text-gold/60 text-center">Auto-saves to your Vault if you leave — or tap Save</p>}
                <UniversalSaveModal open={saveOpen} onClose={() => setSaveOpen(false)} assetId={filmId}
                  track="VIDEOS" isMusic={false} defaultName="Mist sequence"
                  onSaved={() => setSaved(true)} />
              </>
            ) : (
              <button onClick={stitchFilm} disabled={stitching} data-testid="seq-stitch-btn" className={`${btn} w-full bg-gold text-black py-3 disabled:opacity-50`}>
                {stitching ? <Loader2 size={14} className="animate-spin" /> : <Film size={14} />} Combine Into One Film
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
